/**
 * 여행 코스 — 목록과 상세.
 *
 * 목록의 기본 정렬은 popular 인데, 그 값은 조회수가 아니라 **실제로 따라한
 * 수**(copied_count)입니다. TourAPI 인기순이 조회수 기준이라 제주 1위가
 * 가새기오름인 것과 대비됩니다.
 */
import { useCallback, useEffect, useState } from 'react';
import { TravelCourseRepository } from 'repository/travelCourseRepository';
import { unwrapData, unwrapList } from 'common/utils/apiResponse';

export interface CoursePlace {
    coursePlaceId: number;
    dayNumber?: number | null;
    sortOrder?: number | null;
    visitTime?: string | null;
    category?: string | null;
    placeName: string;
    placeAddress?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    photoUrl?: string | null;
    description?: string | null;
    externalPlaceId?: string | null;
}

/** 이 코스를 담을 수 있는 내 여행 */
export interface CourseCopyTarget {
    meetingId: string;
    meetingName: string;
    travelDestination?: string | null;
    travelStartDate?: string | null;
    travelEndDate?: string | null;
    /** 여행 일수 — 코스가 더 길면 뒷날이 접힙니다 */
    days?: number | null;
    /** 이미 담겨 있는 장소 수 */
    placeCount?: number | null;
    alreadyCopied?: boolean | null;
}

export interface CourseReview {
    reviewId: number;
    nickname?: string | null;
    rating: number;
    content?: string | null;
    createdAt?: string | null;
}

/** 담기 결과 — 몇 곳 담고 몇 곳 건너뛰었는지 */
export interface CourseCopyResult {
    addedCount: number;
    skippedCount: number;
    /** 코스가 여행보다 길어 뒷날을 접었는지 */
    folded: boolean;
    targetDays: number;
}

export interface TravelCourse {
    courseId: string;
    title: string;
    summary?: string | null;
    creatorNo?: number | null;
    creatorNickname?: string | null;
    regionId?: string | null;
    regionName?: string | null;
    days?: number | null;
    placeCount?: number | null;
    thumbnailUrl?: string | null;
    /** 실제로 따라한 수 — 조회수가 아닙니다 */
    copiedCount?: number | null;
    ratingAvg?: number | null;
    ratingCount?: number | null;
    shareToken?: string | null;
    createdAt?: string | null;
    places?: CoursePlace[] | null;
    /** 보는 사람이 따라해 본 코스인가 — 별점 자격 */
    copiedByMe?: boolean | null;
    mine?: boolean | null;
    /** 이미 남긴 별점 — 있으면 화면이 그 값으로 시작합니다 */
    myReview?: CourseReview | null;
}

/** 지역별 코스 목록 */
export const useTravelCourses = (
    regionId?: string | null,
    sort: 'popular' | 'rating' | 'recent' = 'popular',
    enabled = true,
) => {
    const [courses, setCourses] = useState<TravelCourse[]>([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const load = useCallback(async () => {
        if (!enabled) return;
        setLoading(true);
        try {
            const res = await TravelCourseRepository.list({
                regionId: regionId || undefined, sort, page: 1, size: 20,
            });
            setCourses(unwrapList<TravelCourse>(res));
        } catch {
            /* 코스를 못 받았다고 여행 만들기가 막히면 안 됩니다 */
            setCourses([]);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [regionId, sort, enabled]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await load();
        })();
        return () => { cancelled = true; };
    }, [load]);

    return { courses, loading, loaded, reload: load };
};

/** 코스 하나 */
export const useTravelCourse = (courseId?: string | null) => {
    const [course, setCourse] = useState<TravelCourse | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!courseId) return;
        setLoading(true);
        try {
            const res = await TravelCourseRepository.get(courseId);
            setCourse(unwrapData<TravelCourse>(res) || null);
        } catch {
            setCourse(null);
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await load();
        })();
        return () => { cancelled = true; };
    }, [load]);

    return { course, loading, reload: load };
};

/**
 * 내 여행으로 담기.
 *
 * 담을 여행 목록을 미리 받아 두지 않고 **버튼을 눌렀을 때** 받습니다.
 * 코스 상세를 여는 사람 대부분은 구경만 하고 나갑니다 — 그 사람들 몫까지
 * 내 여행 목록을 매번 조회할 이유가 없습니다.
 */
export const useCourseCopy = (courseId?: string | null) => {
    const [targets, setTargets] = useState<CourseCopyTarget[]>([]);
    const [loadingTargets, setLoadingTargets] = useState(false);
    const [targetsLoaded, setTargetsLoaded] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadTargets = useCallback(async () => {
        if (!courseId) return;
        setLoadingTargets(true);
        try {
            const res = await TravelCourseRepository.copyTargets(courseId);
            setTargets(unwrapList<CourseCopyTarget>(res));
        } catch {
            setTargets([]);
        } finally {
            setLoadingTargets(false);
            setTargetsLoaded(true);
        }
    }, [courseId]);

    /** @returns 성공하면 담긴 결과, 실패하면 서버가 준 이유 */
    const copy = useCallback(async (
        meetingId: string,
    ): Promise<{ ok: true; result: CourseCopyResult } | { ok: false; message?: string }> => {
        if (!courseId || busy) return { ok: false };
        setBusy(true);
        try {
            const res = await TravelCourseRepository.copy(courseId, meetingId);
            const result = unwrapData<CourseCopyResult>(res);
            /* onRequest 는 에러를 삼켜서 실패도 응답으로 옵니다.
               addedCount 가 없으면 담기지 않은 것입니다. */
            if (!result || typeof result.addedCount !== 'number') {
                return { ok: false, message: (res as any)?.data?.message };
            }
            return { ok: true, result };
        } catch (e: any) {
            return { ok: false, message: e?.response?.data?.message };
        } finally {
            setBusy(false);
        }
    }, [courseId, busy]);

    return { targets, loadingTargets, targetsLoaded, loadTargets, copy, busy };
};

/**
 * 별점 남기기 — 따라해 본 사람만(서버가 403 으로 막습니다).
 *
 * 다시 보내면 덮어씁니다. 다녀와서 생각이 바뀌는 건 정상입니다.
 */
export const useCourseReview = (courseId?: string | null) => {
    const [busy, setBusy] = useState(false);

    const submit = useCallback(async (
        rating: number,
        content?: string,
    ): Promise<{ ok: true; review: CourseReview } | { ok: false; message?: string }> => {
        if (!courseId || busy) return { ok: false };
        setBusy(true);
        try {
            const res = await TravelCourseRepository.writeReview(courseId, rating, content);
            const review = unwrapData<CourseReview>(res);
            if (!review || typeof review.rating !== 'number') {
                return { ok: false, message: (res as any)?.data?.message };
            }
            return { ok: true, review };
        } catch (e: any) {
            return { ok: false, message: e?.response?.data?.message };
        } finally {
            setBusy(false);
        }
    }, [courseId, busy]);

    return { submit, busy };
};

/** 일차별로 묶습니다. 일차 없는 장소는 맨 뒤에 */
export const groupByDay = (places?: CoursePlace[] | null) => {
    const map = new Map<number | null, CoursePlace[]>();
    for (const p of places || []) {
        const key = p.dayNumber ?? null;
        const list = map.get(key);
        if (list) list.push(p); else map.set(key, [p]);
    }
    return Array.from(map.entries())
        .sort((a, b) => {
            if (a[0] === null) return 1;
            if (b[0] === null) return -1;
            return a[0] - b[0];
        })
        .map(([day, items]) => ({
            day,
            items: [...items].sort((x, y) => (x.sortOrder || 0) - (y.sortOrder || 0)),
        }));
};
